import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { MapPinCheck } from "lucide-react";
import { Dancing_Script } from "next/font/google";
import { useTranslation } from "@/contexts/TranslationContext";
import NewsletterSubscribeModal from "./NewsletterSubscribeModal";

const ds = Dancing_Script({
    weight: ["400", "500", "600", "700"],
    subsets: ["latin"],
});

export default function EventsBlock({ products = [] }) {
    const { t } = useTranslation();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedEventTitle, setSelectedEventTitle] = useState("");

    const openSubscribe = (title) => {
        setSelectedEventTitle(title);
        setIsModalOpen(true);
    };

    const closeSubscribe = () => setIsModalOpen(false);

    const events = products.map((p) => {
        const v = p.product_variants?.[0];

        const rawImage = v?.images?.[0]?.url || p.images?.[0]?.url;
        const image = rawImage ? "" + rawImage : "/placeholder.jpg";

        const seatmap = v?.product_variant_attributes?.find(a => a.attribute?.key === "seatmap");

        const eventStartAttr = v?.product_variant_attributes?.find(
            (a) => a.attribute?.key === "event_start_time"
        );

        const rawStart = eventStartAttr?.custom_value_date ?? null;

        const dateObj = rawStart ? new Date(rawStart) : null;

        const formattedDate =
            dateObj && !Number.isNaN(dateObj.getTime())
                ? new Intl.DateTimeFormat("hu-HU", {
                    timeZone: "Europe/Budapest", // 🔥 EZ A LÉNYEG
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                }).format(dateObj)
                : null;


        const locationKey = seatmap?.attribute_value?.key ?? null;

        const comingSoon = p.product_status === "coming_soon";

        const promoRibbon = p.promo_ribbon_text || null;

        return {
            id: p.id,
            image,
            title: p.name,
            locationKey,
            comingSoon,
            promoRibbon,
            date: formattedDate,
            button: comingSoon
                ? { color: "bg-[#f97316]", link: null }
                : { color: "bg-[#fe64db]", link: "/" + p.slug },
        };
    });


    return (
        <section id="esemenyek" className="scroll-mt-15 bg-black py-10 px-4 sm:px-6 lg:px-8">
            <div className="w-full mx-auto">
                <h2 className={`${ds.className} text-3xl md:text-4xl font-light text-center text-[#fe64db] mb-8`}>
                    {t("events.current")}
                </h2>

                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8 gap-x-3 gap-y-6 sm:gap-x-6 sm:gap-y-6">
                    {events.map((event) => {
                        const isExternal = event.button.link?.startsWith("http");

                        return (
                            <div key={event.id} className="flex flex-col items-center">
                                {/* Kép */}
                                {event.comingSoon ? (
                                    <div
                                        className={
                                            "w-full aspect-square mb-4 overflow-hidden rounded-lg relative " +
                                            (event.comingSoon ? "cursor-not-allowed" : "cursor-pointer")
                                        }
                                    >
                                        <Image
                                            src={event.image}
                                            alt={event.title}
                                            fill
                                            unoptimized
                                            className="object-cover rounded-lg transition-transform duration-300 hover:scale-105"
                                        />

                                        {event.promoRibbon && (
                                            <div
                                                className="
      absolute top-3 -left-8 rotate-[-20deg]
      bg-[#facc15] text-black font-bold shadow-lg pointer-events-none

      px-8 py-1.5 text-xs
      lg:top-2 lg:-left-6 lg:px-6 lg:py-1 lg:text-[11px]
    "
                                            >
                                                {event.promoRibbon}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <Link
                                        href={event.button.link}
                                        className="w-full aspect-square mb-4 overflow-hidden rounded-lg relative block cursor-pointer"
                                    >
                                        <Image
                                            src={event.image}
                                            alt={event.title}
                                            fill
                                            unoptimized
                                            className="object-cover rounded-lg transition-transform duration-300 hover:scale-105"
                                        />

                                        {event.promoRibbon && (
                                            <div
                                                className="
      absolute top-3 -left-8 rotate-[-20deg]
      bg-[#facc15] text-black font-bold shadow-lg pointer-events-none

      px-8 py-1.5 text-xs
      lg:top-2 lg:-left-6 lg:px-6 lg:py-1 lg:text-[11px]
    "
                                            >
                                                {event.promoRibbon}
                                            </div>
                                        )}
                                    </Link>
                                )}

                                {/* Helyszín + dátum (fix 2 sor, nagy ikon bal oldalt) */}
                                <div className="w-full text-white text-sm mb-2 italic flex items-start gap-3">
                                    <MapPinCheck className="w-6 h-6 text-[#fe64db] shrink-0 mt-0.5" />

                                    <div className="min-w-0 flex-1">
                                        {/* 1. sor: helyszín, 1 sor + ... */}
                                        <div className="w-full min-w-0 overflow-hidden whitespace-nowrap text-ellipsis">
                                            {event.locationKey
                                                ? t(`seatmap.${event.locationKey}`, event.locationKey)
                                                : t("event.location.tbd")}
                                        </div>

                                        {/* 2. sor: dátum — mindig foglaljon helyet */}
                                        <div className="text-xs text-white/70 not-italic leading-tight h-[1.25em]">
                                            {event.date ? event.date : <span className="invisible">00.00.0000</span>}
                                        </div>
                                    </div>
                                </div>


                                {/* Gomb */}
                                {event.comingSoon ? (
                                    <button
                                        type="button"
                                        onClick={() => openSubscribe(event.title)}   // ✅ itt
                                        className={`flex items-center justify-center px-4 py-2 text-white rounded ${event.button.color}
                      hover:opacity-90 transform transition-transform duration-300 hover:scale-105 cursor-pointer animate-pulse`}
                                    >
                                        <span className="text-2xl font-bold mr-2">i</span>
                                        {t("eventsblock.notify_me")}
                                    </button>
                                ) : (
                                    <Link
                                        href={event.button.link}
                                        passHref
                                        target={isExternal ? "_blank" : undefined}
                                        rel={isExternal ? "noopener noreferrer" : undefined}
                                        className={`flex items-center justify-center px-4 py-2 text-white rounded hover:opacity-90 transform transition-transform duration-300 hover:scale-105 ${event.button.color} animate-pulse`}
                                    >
                                        <span className="text-2xl font-bold mr-2">i</span>
                                        {t("event.book_now")}
                                    </Link>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <NewsletterSubscribeModal
                open={isModalOpen}
                onClose={closeSubscribe}
                eventTitle={selectedEventTitle} // ✅ ide jön a coming soon neve, pl. "Morzsi party"
            />
        </section>
    );
}